import { useEffect, useState } from 'react'
import { f2 } from '../../copyFlow2'
import { calOne } from '../../copyCalendar'
import { r3f } from '../../copyR3Filters'
import { downloadIcs } from '../../lib/calendar'
import { chooseCalendar, chosenCalendar, saveCalendarChoice, type CalApp } from '../../lib/calendarChoice'
import { applePlatform, googleCalendarUrl, newAddId, recordAdd } from '../../lib/growth'
import { ensureSession } from '../../lib/api'
import { useLaneFlag } from '../../lib/laneFlag'
import type { Card } from '../../lib/browse'
import { currentZone } from '../../lib/plan'
import { Calendar, Check } from '../../ui/icons'
import { buttonClass } from '../../ui/styles'

/**
 * Calendar from detail (V2 audit fix): one activity straight to Apple Calendar (.ics) or Google Calendar, without a plan.
 * Each tap records a calendar add (the aha moment) with one id per card.
 * Blind rounds 1-2 (marco, lena, 10/10): the tap showed only a small grey "Finish adding your plan" line and the
 * calendar_adds POST ended ERR_ABORTED. Now: a session exists before the add (a visitor got no row at all), the insert is
 * awaited (up to 4 s) BEFORE the .ics download starts, and the tapped button turns into a check + what happened, with one
 * line saying the one step left. Single-activity words (copyCalendar.ts, Astra); the plan sheet keeps its plan wording.
 *
 * R-23 / R-24 (Round 3, Lane 3; AGENTS.md Core flow: "a single thing can also go straight to the calendar; the calendar
 * button matches the calendar they use"): the first time, it asks which calendar they use with two equal buttons, and the
 * tap that answers is also the add (one tap). From then on their calendar is the one full button (one tap) and the other
 * is a small "instead" link; using the link switches what is remembered. Remembered on this phone and on the profile
 * (lib/calendarChoice.ts); Settings changes it. Flag calendar_memory off: the older look (the platform's own leads).
 */
export function DetailCalendar({ card }: { card: Card }) {
  const item = card.item
  const memory = useLaneFlag('calendar_memory')
  const [addId] = useState(newAddId)
  const [now] = useState(Date.now)
  const [done, setDone] = useState<null | CalApp>(null)
  const [busy, setBusy] = useState(false)
  // read once per card: answering the question must not swap the buttons under the finger
  const [chosen] = useState(chosenCalendar)
  useEffect(() => { if (memory && chosen) void saveCalendarChoice() }, [memory, chosen]) // a Settings change reaches the profile
  const gone = item.kind === 'event' && !!(item.end_at ?? item.start_at) && new Date(item.end_at ?? item.start_at!).getTime() < now
  if (gone) return null
  const tz = currentZone() // the activity's city (review M2), set by the Browse answer on screen
  const record = async (method: CalApp) => {
    try { await ensureSession() } catch { /* the add is still offered; it just is not counted */ }
    const { sent } = recordAdd(item, method, addId, undefined, undefined, undefined, false)
    await Promise.race([sent, new Promise((r) => setTimeout(r, 4000))])
  }
  const apple = async () => {
    if (busy) return
    setBusy(true)
    if (memory) chooseCalendar('ics')
    await record('ics') // recorded before the download hands off to the Calendar app
    downloadIcs(item, addId, null, tz)
    setDone('ics')
    setBusy(false)
  }
  // Google opens in a new tab at the tap (a browser only allows that inside the tap); this page stays, so the insert finishes
  const google = () => { if (memory) chooseCalendar('google'); void record('google'); setDone('google') }
  const mark = (on: boolean, label: string, doneLabel: string) => (on ? <><Check size={18} />{doneLabel}</> : label)
  // asked once: no calendar remembered yet, so both are full buttons under the question
  const asking = memory && !chosen && !done
  const lead: CalApp = (memory ? chosen : null) ?? (chosenCalendar() ?? (applePlatform() ? 'ics' : 'google'))
  const look = (m: CalApp) => asking
    ? buttonClass('secondary', 'md', 'w-full')
    : m === lead
      ? buttonClass(done === m ? 'primary' : 'secondary', 'md', 'w-full')
      : 'press t-control mt-1 inline-flex min-h-11 items-center gap-1 text-accent-ink underline underline-offset-4'
  // the small link names what it does once a calendar is remembered ("Use Google Calendar instead")
  const label = (m: CalApp) => (memory && chosen && m !== lead
    ? r3f(m === 'google' ? 'r3f.cal.other_google' : 'r3f.cal.other_apple')
    : f2(m === 'ics' ? 'calendar.apple' : 'calendar.google'))
  const a = (
    <button key="a" type="button" onClick={() => void apple()} disabled={busy} data-review-id="cal-apple" data-lead={(!asking && lead === 'ics') || undefined} aria-live="polite"
      className={look('ics')}>
      {mark(done === 'ics', label('ics'), calOne('calendar.one_apple_done'))}
    </button>
  )
  const g = (
    <a key="g" href={googleCalendarUrl(item, addId, undefined, undefined, tz)} target="_blank" rel="noopener noreferrer" onClick={google} data-review-id="cal-google" data-lead={(!asking && lead === 'google') || undefined}
      className={look('google')}>
      {mark(done === 'google', label('google'), calOne('calendar.one_google_done'))}
    </a>
  )
  // asking: the platform's own calendar first, side by side from 440 up; otherwise the remembered one leads
  const first = asking ? (applePlatform() ? 'ics' : 'google') : lead
  return (
    <section data-review-id="detail-calendar" data-asking={asking || undefined} className="mt-4 border-t border-line pt-3">
      <p className="t-label flex items-center gap-2 text-ink"><Calendar size={16} />{f2('listing.calendar')}</p>
      {asking && <p data-review-id="cal-ask" className="t-meta mt-1 text-ink-2">{r3f('r3f.cal.ask')}</p>}
      {/* flow audit #17: the calendar they use leads; the other sits beneath as a link */}
      <div className={asking ? 'mt-2 grid gap-2 min-[440px]:grid-cols-2' : 'mt-2 flex flex-col items-start'}>{first === 'ics' ? [a, g] : [g, a]}</div>
      {done && (
        <p role="status" data-review-id="cal-confirm" className="t-body mt-3 border-l-[3px] border-accent pl-3 text-ink">
          {calOne(done === 'ics' ? 'calendar.one_apple_line' : 'calendar.one_google_line')}
          {memory && !chosen && <span data-review-id="cal-remembered" className="t-meta mt-1 block text-ink-2">{r3f('r3f.cal.switch_note')}</span>}
        </p>
      )}
    </section>
  )
}
